import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Lets a page point the header's "Get Started" at its own primary action.
 *
 * On a campaign landing page the header button is a second primary CTA; left
 * at plain sign-up it competes with the page's own and drops what the page
 * knows (the chosen template, the visitor's utm_* params). A page calls
 * useNavCta(href) and the header — desktop bar and mobile menu — uses that
 * href while the page is mounted. Everywhere else nothing changes.
 */
const NavCtaContext = createContext({ href: null, setHref: () => {} });

export const NavCtaProvider = ({ children }) => {
    const [href, setHref] = useState(null);
    const value = useMemo(() => ({ href, setHref }), [href]);
    return <NavCtaContext.Provider value={value}>{children}</NavCtaContext.Provider>;
};

/** The header's side: the override, or the default. */
export const useNavCtaHref = (fallback) => useContext(NavCtaContext).href || fallback;

/** Whether a page has claimed the header CTA (a minimal header keeps the button then). */
export const useNavCtaClaimed = () => Boolean(useContext(NavCtaContext).href);

/** The page's side: sets the header CTA while mounted. */
export const useNavCta = (href) => {
    const { setHref } = useContext(NavCtaContext);
    useEffect(() => {
        setHref(href);
        return () => setHref(null);
    }, [href, setHref]);
};
